import { Link } from "@inertiajs/react"
import { useForm } from "@/lib/use-form"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Field, FieldGroup } from "@/components/ui/field"
import { LandingPage } from "@/components/landing-page"
import type { LandingUsersPasswordNewProps } from "@/types/generated"

const PasswordNewPage = ({ user, organizationName }: LandingUsersPasswordNewProps) => {
  const { t } = useTranslation()

  const form = useForm({
    "user[email]": user.email ?? ""
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post("/users/password", { forceFormData: true })
  }

  const allErrors = Object.values(user.errors ?? {}).flat()

  return (
    <LandingPage organizationName={organizationName}>
      <div className="flex flex-col gap-16">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold">{t("passwords.new.title")}</h1>
          <p className="text-muted-foreground text-sm text-balance">{t("passwords.new.subtitle")}</p>
        </div>

        {allErrors.length > 0 && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <ul className="list-inside list-disc">
              {allErrors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <FieldGroup>
            <Field>
              <Label htmlFor="password_new_email">{t("passwords.new.email")}</Label>
              <Input
                id="password_new_email"
                type="email"
                autoComplete="email"
                value={form.data["user[email]"]}
                onChange={(e) => form.setData("user[email]", e.target.value)}
                required
              />
            </Field>

            <Button type="submit" className="w-full" disabled={form.processing}>
              {form.processing ? t("passwords.new.submitting") : t("passwords.new.submit")}
            </Button>
          </FieldGroup>
        </form>

        <div className="text-center text-sm">
          <Link href="/login" className="text-muted-foreground hover:text-primary hover:underline">
            {t("passwords.new.back_to_login")}
          </Link>
        </div>
      </div>
    </LandingPage>
  )
}

export default PasswordNewPage
